import { useLayoutEffect, useState } from 'react';

export type ElementBoundingRect = {
  x: number;
  y: number;
  width: number;
  height: number;
  top: number;
  right: number;
  bottom: number;
  left: number;
};

const readRect = (el: Element): ElementBoundingRect => {
  const r = el.getBoundingClientRect();
  return {
    x: r.x,
    y: r.y,
    width: r.width,
    height: r.height,
    top: r.top,
    right: r.right,
    bottom: r.bottom,
    left: r.left
  };
};

/**
 * Frames to keep re-querying for a match before giving up. The element can mount
 * a commit later than the component measuring it, and a lookup that finds
 * nothing has no observer to bring it back later — so without a retry the caller
 * stays on its fallback height. Bounded rather than open-ended, accepting that a
 * match arriving after this many frames is missed: a selector that never matches
 * must not poll for the life of the component.
 */
export const MAX_RESOLVE_FRAMES = 60;

/**
 * @description
 * Tracks the bounding client rect of the first element that matches the selector, updating when
 * the element resizes or the window is resized. Safe for SSR.
 * @param selector - CSS selector for the element to measure, or null/undefined to skip
 * @returns The element's bounding client rect, or null if there is no DOM, no selector, or no match
 */
const useBoundingRectBySelector = (
  selector: string | null | undefined
): ElementBoundingRect | null => {
  const hasWindow = globalThis.window !== undefined;
  const hasDocument = globalThis.document !== undefined;
  const [rect, setRect] = useState<ElementBoundingRect | null>(null);

  useLayoutEffect(() => {
    if (!hasWindow || !hasDocument || !selector) {
      setRect(null);
      return undefined;
    }

    let resizeObserver: ResizeObserver | undefined;
    let target: Element | null = null;
    let frame = 0;
    let framesLeft = MAX_RESOLVE_FRAMES;

    /** Stable identity so the cleanup below detaches the listener it added. */
    const sync = () => {
      if (target) {
        setRect(readRect(target));
      }
    };

    const observe = () => {
      target = globalThis.document.querySelector(selector);
      if (!target) {
        setRect(null);
        if (framesLeft > 0) {
          framesLeft -= 1;
          frame = globalThis.requestAnimationFrame(observe);
        }
        return;
      }

      sync();

      resizeObserver = new ResizeObserver(sync);
      resizeObserver.observe(target);

      globalThis.window.addEventListener('resize', sync);
    };

    observe();

    return () => {
      globalThis.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      globalThis.window.removeEventListener('resize', sync);
    };
  }, [hasWindow, hasDocument, selector]);

  return rect;
};

export default useBoundingRectBySelector;
